import { WorkflowType } from "@/pages/query/types";
import { FC } from "react";
import { Stack } from "@altimateai/lego";
import { CustomWorkloadsGraph } from "@/pages/query/custom-workloads/CustomWorkloadsGraph";
import CustomWorkloadsJobsTable from "@/pages/query/custom-workloads/CustomWorkloadsJobsTable";
import { useQuery } from "@tanstack/react-query";
import { getSnowflakeJobFilters } from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { DatatableContextProvider } from "@/components/tables/contexts/DatatableContextProvider";
import { Typography } from "@altimateai/lego";
import SpacesRedirectionButton from "@/components/spaces/SpacesRedirectionButton";
import { SPACES_TAG_MAPPING } from "@/helpers/spacesConstants";
import { DateRange } from "@/components/dateRange";

interface CustomWorkloadsJobsProps {
  workflowType: WorkflowType;
}

export const CustomWorkloadsJobs: FC<CustomWorkloadsJobsProps> = ({ workflowType }) => {
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { data: filters } = useQuery({
    queryKey: ["snowflake_jobs", "filters", startDate, endDate, workflowType],
    queryFn: () =>
      getSnowflakeJobFilters({
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workflow_type: workflowType,
      }),
  });

  return (
    <DatatableContextProvider>
      <Stack direction={"column"} className={"al-bg-white al-p-3 al-rounded"}>
        <Stack align={"center"} justify={"between"} className={"al-w-full"}>
          <Stack direction={"row"} align={"center"} gap={"md"} className={"al-my-2"}>
            <Typography variant={"section-heading"}>Jobs</Typography>
            <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.WORKLOADS} />
          </Stack>
          <Stack direction={"row"} align={"center"}>
            <DateRange
              variant={"inline"}
              startDate={startDate}
              endDate={endDate}
              availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
              onDateRangeSelect={onUpdate}
            />
            <div id={"workloads-filter-container"} />
          </Stack>
        </Stack>
        <Stack direction={"column"} gap={"xl"}>
          <CustomWorkloadsGraph filters={filters} workflowType={workflowType} />
          <CustomWorkloadsJobsTable filters={filters} workflowType={workflowType} />
        </Stack>
      </Stack>
    </DatatableContextProvider>
  );
};
